
import { defineComponent, onBeforeMount, ref, watch, onMounted } from "vue";
import { hideModal, modalHideListener } from "@/core/helpers/dom";
import { ApiBase, ApiAccount } from "@/core/api";
import mixin from "@/mixins";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "add-key-parameters-modal",
  props: {
    id: { type: Number, default: 0 },
    accountId: { type: Number, default: 0 },
  },
  components: {},
  emits: ["update-list", "reset-form"],
  setup(props, { emit }) {
    const formRef = ref<null | HTMLFormElement>(null);
    const addAddressModalRef = ref<null | HTMLElement>(null);
    const submitButton = ref<HTMLElement | null>(null);
    const loading = ref<boolean>(false);
    const addressTypeOptions = ref([]);
    const countryOptions = ref([]);
    const { t } = useI18n();
    const {
      showValidateErrorMsg,
      showFormSubmitSuccessMsg,
      showServerErrorMsg,
    } = mixin();

    const formData = ref({
      id: props.id,
      account_id: props.accountId,
      name: "",
      street: "",
      zip_code: "",
      city: "",
      country_iso_2: "",
      type: "",
    });

    watch(
      () => props.id,
      (newVal) => {
        if (newVal) {
          getFormData(newVal);
        } else {
          resetForm();
        }
      }
    );

    const rules = ref({
      name: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
      street: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
      city: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
      country_iso_2: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
    });

    const getFormData = (id) => {
      if (id) {
        loading.value = true;
        ApiAccount.getAddressInfo({ id: id })
          .then(({ data }) => {
            loading.value = false;
            if (data.code == 0) {
              formData.value = data.data;
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    };

    const getDropdownOptions = async () => {
      const { data } = await ApiBase.getTaggingData({
        short_key: "global_address_type",
      });
      if (data.code == 0) {
        addressTypeOptions.value = data.data.items;
      }
    };

    const getCountryOptions = async () => {
      const { data } = await ApiBase.getCountryData();
      if (data.code == 0) {
        countryOptions.value = data.data;
      }
    };

    const submit = () => {
      if (!formRef.value) {
        return;
      }

      formRef.value.validate((valid) => {
        if (valid) {
          loading.value = true;
          if (submitButton.value) {
            submitButton.value.setAttribute("data-kt-indicator", "on");
          }
          if (props.id) {
            editKeyarameters();
          } else {
            addKeyarameters();
          }
        } else {
          showValidateErrorMsg();
          return false;
        }
      });
    };

    const addKeyarameters = () => {
      ApiAccount.addAddress(formData.value)
        .then(({ data }) => {
          loading.value = false;
          submitButton.value?.removeAttribute("data-kt-indicator");
          if (data.code == 0) {
            showFormSubmitSuccessMsg(() => {
              resetForm();
              hideModal(addAddressModalRef.value);
              emit("update-list");
            });
          } else {
            showServerErrorMsg(data);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const editKeyarameters = () => {
      ApiAccount.updateAddress(formData.value)
        .then(({ data }) => {
          loading.value = false;
          submitButton.value?.removeAttribute("data-kt-indicator");
          if (data.code == 0) {
            showFormSubmitSuccessMsg(() => {
              resetForm();
              hideModal(addAddressModalRef.value);
              emit("update-list");
            });
          } else {
            showServerErrorMsg(data);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const handleDiscard = () => {
      hideModal(addAddressModalRef.value);
      resetForm();
      emit("reset-form");
    };

    const resetForm = () => {
      formRef.value?.resetFields();
    };

    onMounted(() => {
      modalHideListener(addAddressModalRef.value, () => {
        emit("reset-form");
      });
    });

    onBeforeMount(() => {
      getDropdownOptions();
      getCountryOptions();
    });

    return {
      loading,
      formData,
      addressTypeOptions,
      countryOptions,
      rules,
      formRef,
      addAddressModalRef,
      submitButton,
      props,
      t,
      getFormData,
      submit,
      handleDiscard,
      resetForm,
    };
  },
});
